import type { MfaStage } from '@/stores/auth'

/**
 * One function decides every navigation, so the order of the checks is visible
 * in one place rather than spread over three hooks. It is pure: the router
 * hook reads the store and hands the answer in, which is also what makes each
 * rule testable without mounting a router.
 */

export interface RouteMeta {
  /** Reachable with no token at all, such as sign-in or password reset. */
  public?: boolean
  /** Part of the multi-factor flow; reachable with a pre-MFA token only. */
  stage?: 'mfa'
  /** Permission string the user must hold, as `/auth/me` spells it. */
  permission?: string
  /** Needs a Google enterprise: devices, enrolment, policies. */
  requiresEnterprise?: boolean
  /**
   * Swaps the session for one handed over by another app, so it must open
   * whatever this browser holds: nothing, a live session, or a half-finished
   * sign-in. It is the one page no other rule may redirect.
   */
  handoff?: boolean
}

export interface RouteIntent {
  name: string
  fullPath: string
  meta: RouteMeta
}

export interface SessionView {
  isAuthenticated: boolean
  mfaStage: MfaStage | null
  can: (permission: string) => boolean
  /**
   * Whether the tenant has bound a Google enterprise. `null` means the answer
   * has not arrived, or does not apply: an operator has no enterprise, and a
   * tenant user who cannot read one is never asked.
   */
  enterpriseBound?: boolean | null
}

export type Redirect = { name: string; query?: Record<string, string> }

const HOME = 'dashboard'
const SIGN_IN = 'sign-in'
const FORBIDDEN = 'forbidden'
const ONBOARDING = 'enterprise-onboarding'

const MFA_PAGE: Record<Exclude<MfaStage, 'verified'>, string> = {
  setup_required: 'mfa-setup',
  verify_required: 'mfa-verify',
}

type PendingStage = Exclude<MfaStage, 'verified'>

function pendingStage(session: SessionView): PendingStage | null {
  return session.mfaStage === 'setup_required' || session.mfaStage === 'verify_required'
    ? session.mfaStage
    : null
}

/** A half-authenticated token holds the MFA pages and nothing else. */
function resolvePending(to: RouteIntent, stage: PendingStage): true | Redirect {
  return to.meta.stage === 'mfa' ? true : { name: MFA_PAGE[stage] }
}

/** Sign-in and the MFA pages are finished business for a live session. */
function resolveSignedIn(to: RouteIntent, session: SessionView): true | Redirect {
  if (to.meta.public === true || to.meta.stage === 'mfa') return { name: HOME }
  return resolveAccess(to, session)
}

function resolveAccess(to: RouteIntent, session: SessionView): true | Redirect {
  if (to.meta.permission !== undefined && !session.can(to.meta.permission)) {
    return { name: FORBIDDEN }
  }
  // Only a definite "not bound" diverts. Unknown lets the page open and show
  // its own loading state, because the alternative is bouncing every cold
  // load through onboarding before the binding has been read.
  if (to.meta.requiresEnterprise === true && session.enterpriseBound === false) {
    return { name: ONBOARDING }
  }
  return true
}

export function resolveNavigation(to: RouteIntent, session: SessionView): true | Redirect {
  if (to.meta.handoff === true) return true

  // Pending MFA is checked before `public` so a pre-MFA user cannot wander
  // back to sign-in and leave a live token behind.
  const pending = pendingStage(session)
  if (pending !== null) return resolvePending(to, pending)

  if (session.isAuthenticated) return resolveSignedIn(to, session)

  if (to.meta.public === true) return true

  // Carry the whole path, query included, so the user lands where they aimed.
  return { name: SIGN_IN, query: { redirect: to.fullPath } }
}

/**
 * Narrows the `redirect` query parameter to a path this app could have written.
 *
 * The value reaches the sign-in page from the address bar, so a crafted link
 * could otherwise bounce a freshly authenticated user to another origin. Only
 * a single leading slash is a path: `//host` and `/\\host` are both read as
 * another origin by at least one browser.
 */
export function safeRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//') || value.startsWith('/\\')) return null
  if (value.includes('\\')) return null
  return value
}
